import { documentFromResponse } from '../components/documents/documentFormats.js';
import { effortForRequest } from '../utils/effortPreference.js';

// import.meta.env n'existe que sous Vite : le module reste importable par les tests node
const API_BASE_URL = (import.meta.env && import.meta.env.VITE_API_URL) || 'http://localhost:3001';

// Événements du protocole SSE v2 (contrat C0, §1.1) relayés tels quels à l'appelant
const SSE_EVENTS = new Set([
  'start', 'thinking_delta', 'text_delta', 'turn_to_reasoning',
  'step_start', 'step_done',
  'artifact_pending', 'artifact', 'artifact_error',
  'usage', 'done', 'error', 'end',
  // Sprint 6 (contrat v1.4, ajouts compatibles v1.3) : contexte condensé, continuation
  'context_compacted', 'continuation',
  // v1.8 (MCP-13) : avancement d'une étape, jamais enregistré
  'step_progress',
  // v1.13 : liens vérifiés de fichiers (photos de véhicules), servis par le même domaine
  'file_links',
  // Lot 1 (vitesse) : phase vraie de l'en-tête et étape annoncée dès le début du bloc d'outil,
  // jamais enregistrées
  'phase', 'step_pending',
]);

// Compatibilité avec l'ancien flux (v1) le temps du déploiement du backend :
// on traduit `chunk`, `tool_use` et `tool_done` en événements v2
function translateLegacyEvent(type, data, legacy) {
  switch (type) {
    case 'chunk':
      return [['text_delta', { turn: 0, text: data.text ?? '' }]];
    case 'tool_use':
      return (data.tools ?? []).map((tool) => {
        const step_id = `legacy-${++legacy.counter}`;
        legacy.running.push({ step_id, tool });
        return ['step_start', { step_id, tool, label: tool, params: {} }];
      });
    case 'tool_done': {
      const index = legacy.running.findIndex((s) => s.tool === data.tool);
      if (index === -1) return [];
      const [step] = legacy.running.splice(index, 1);
      return [['step_done', {
        step_id: step.step_id,
        status: data.error ? 'error' : 'ok',
        ...(data.error ? { error: data.error } : {}),
      }]];
    }
    default:
      return [];
  }
}

/**
 * Lecteur SSE partagé, sorti de streamMessage : flux du chat et flux des notifications (agents,
 * P1), tous deux lus par fetch (EventSource ne pose pas les en-têtes de contexte, et le hash
 * n'entre jamais dans une URL). Lit `response.body` jusqu'à sa fin et appelle
 * onEvent(type, data) pour chaque événement dont le nom est dans `connus` (tous si `connus`
 * est absent) ; `data` vaut {} quand le JSON est null.
 * Ignorés sans erreur : les commentaires (« : ping », aucune ligne data), un JSON illisible,
 * un nom hors de `connus`. Fins de ligne CRLF acceptées ; un dernier événement non suivi
 * d'une ligne vide est lu à la fermeture. Une coupure ou une annulation (signal du fetch)
 * rejette la promesse, comme reader.read().
 * @param {Response} response
 * @param {(type: string, data: object) => void} onEvent
 * @param {Set<string>|null} [connus]
 */
export async function readSse(response, onEvent, connus = null) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';

  const dispatch = (part) => {
    let eventType = 'message';
    const dataLines = [];

    for (const line of part.split('\n')) {
      if (line.startsWith('event:')) eventType = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).replace(/^ /, ''));
    }

    // Commentaires SSE (keep-alive « : ping ») : aucune ligne data, ignorés
    if (dataLines.length === 0) return;
    let data;
    try { data = JSON.parse(dataLines.join('\n')); } catch { return; }

    if (connus && !connus.has(eventType)) return;
    onEvent(eventType, data ?? {});
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, '\n');
    const parts = buffer.split('\n\n');
    buffer = parts.pop();

    for (const part of parts) {
      if (part.trim()) dispatch(part);
    }
  }

  // Dernier événement éventuellement non suivi d'une ligne vide
  if (buffer.trim()) dispatch(buffer);
}

class ChatAPI {
  async sendMessage(messages, context = null, modelId = null) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages,
          ...(context ? { context } : {}),
          ...(modelId ? { modelId } : {}),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to send message');
      }

      return await response.json();
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }

  /**
   * Envoie la conversation et lit le flux SSE v2.
   * @param {Array<{role, content}>} messages  Historique (rôle + réponse finale uniquement)
   * @param {object|null} context  {entreprise_id, user_id, email, user_api_hash} — injecté côté serveur
   * @param {AbortSignal} signal   Annulation (bouton Stop)
   * @param {(type: string, data: object) => void} onEvent  Appelé pour chaque événement du contrat
   * @param {string|null} modelId
   * @param {string|null} conversationId  Conversation de l'historique à laquelle rattacher la réponse
   * @param {{effort?: string|null, conversationEnCreation?: string|null}} options  effort (sprint 6) :
   *   "rapide" | "standard" | "approfondi" ; « auto », absent ou invalide : champ omis (le serveur
   *   choisit). conversationEnCreation (lot 1) : conversation créée en même temps que ce flux
   *   (1er message), rattachée par le serveur dès qu'elle existe.
   */
  async streamMessage(messages, context, signal, onEvent, modelId = null, conversationId = null, { effort = null, conversationEnCreation = null } = {}) {
    const effortField = effortForRequest(effort);
    const response = await fetch(`${API_BASE_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        messages,
        ...(context ? { context } : {}),
        ...(modelId ? { modelId } : {}),
        ...(conversationId ? { conversation_id: conversationId } : {}),
        ...(!conversationId && conversationEnCreation ? { conversation_en_creation: conversationEnCreation } : {}),
        ...(effortField ? { effort: effortField } : {}),
      }),
      signal,
    });

    // Une erreur de requête (ex. modèle inconnu) arrive en SSE avec un statut 4xx :
    // on lit alors le flux pour récupérer l'événement `error`
    const isEventStream = (response.headers.get('content-type') || '').includes('text/event-stream');
    if (!response.ok && !isEventStream) {
      const error = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    // Tous les noms passent (connus absent) : un événement hors contrat peut encore venir de
    // l'ancien flux, traduit ici ; les autres sont ignorés sans erreur, comme avant
    const legacy = { counter: 0, running: [] };
    await readSse(response, (eventType, data) => {
      if (SSE_EVENTS.has(eventType)) {
        onEvent(eventType, data);
      } else {
        for (const [type, payload] of translateLegacyEvent(eventType, data, legacy)) {
          onEvent(type, payload);
        }
      }
    });
  }

  /**
   * Enregistre un avis 👍 / 👎 (contrat C0, §3).
   * @param {{message_id, rating: 1|-1, reason?, comment?, context?}} feedback
   */
  async sendFeedback(feedback) {
    const response = await fetch(`${API_BASE_URL}/api/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(feedback),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return response.json().catch(() => ({}));
  }

  /**
   * Annule un avis déjà enregistré (nouveau clic sur le même pouce).
   * Le contexte (entreprise + hash) part dans le corps, jamais dans l'URL : le serveur
   * vérifie que la réponse appartient bien à ce demandeur.
   */
  async deleteFeedback(messageId, context = null) {
    const response = await fetch(`${API_BASE_URL}/api/feedback/${encodeURIComponent(messageId)}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(context ? { context } : {}),
    });

    if (!response.ok && response.status !== 404) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || error.message || `HTTP ${response.status}`);
    }

    return true;
  }

  /**
   * Modèles proposés et leur disponibilité (contrat v1.6). `quiet` : relecture périodique,
   * sans message en console si le serveur ne répond pas.
   */
  async getModels({ quiet = false } = {}) {
    try {
      const response = await fetch(`${API_BASE_URL}/api/models`, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error('Failed to fetch models');
      }

      return await response.json();
    } catch (error) {
      if (!quiet) console.error('Error fetching models:', error);
      throw error;
    }
  }

  async getTools() {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools`);

      if (!response.ok) {
        throw new Error('Failed to fetch tools');
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching tools:', error);
      throw error;
    }
  }

  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);

      if (!response.ok) {
        throw new Error('Health check failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  }
}

export const chatAPI = new ChatAPI();

// ════════════════════════════════════════════════════════════════════
// Historique des conversations (Sprint 2)
// Le demandeur est identifié par les en-têtes X-Entreprise-Id et X-User-Api-Hash :
// jamais dans l'URL (journaux nginx), jamais mis en cache (Cache-Control: no-store).
// ════════════════════════════════════════════════════════════════════

/** Erreur HTTP de l'API : statut et code métier (`NOT_FOUND`, `TOO_LARGE`…). */
export class ApiError extends Error {
  constructor(message, { status = 0, code = null, details = null } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    // Détail par élément (panier de rapport refusé) : [{id, raison}]
    this.details = details;
  }
}

/** L'historique n'est disponible qu'avec un contexte complet (entreprise + hash). */
export function hasHistoryContext(context) {
  return !!(context?.entreprise_id && context?.user_api_hash);
}

function contextHeaders(context) {
  return {
    'X-Entreprise-Id': String(context.entreprise_id),
    'X-User-Api-Hash': String(context.user_api_hash),
  };
}

async function conversationRequest(path, context, { method = 'GET', body, signal, keepalive = false } = {}) {
  if (!hasHistoryContext(context)) {
    throw new ApiError('Contexte entreprise requis', { status: 401, code: 'CONTEXT_REQUIRED' });
  }
  const response = await fetch(`${API_BASE_URL}/api/conversations${path}`, {
    method,
    headers: {
      ...contextHeaders(context),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    cache: 'no-store',
    signal,
    keepalive,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new ApiError(data.error || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
  }
  return data;
}

const enc = encodeURIComponent;

export const conversationsAPI = {
  /** {conversations: [...], next_cursor} — la plus récente d'abord. */
  list({ q = '', limit = 30, cursor = null } = {}, context, signal) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (q.trim()) params.set('q', q.trim());
    if (cursor) params.set('cursor', cursor);
    return conversationRequest(`?${params}`, context, { signal });
  },

  /** {conversation, messages: [{id, role, content, ui, message_id, feedback}]} */
  get(id, context, signal) {
    return conversationRequest(`/${enc(id)}`, context, { signal });
  },

  /**
   * {conversation} — titre vide : le serveur le tire du 1er message (sans appel au modèle).
   * id (lot 1) : identifiant proposé par l'interface, pour lancer la réponse en même temps.
   */
  create({ model_id = null, id = null } = {}, context) {
    return conversationRequest('', context, { method: 'POST', body: { ...(model_id ? { model_id } : {}), ...(id ? { id } : {}) } });
  },

  /** {conversation} — renommage ou archivage. */
  update(id, patch, context) {
    return conversationRequest(`/${enc(id)}`, context, { method: 'PATCH', body: patch });
  },

  remove(id, context) {
    return conversationRequest(`/${enc(id)}`, context, { method: 'DELETE' });
  },

  /** Liste complète et ordonnée des messages (idempotent). {ok, changed, conversation} */
  saveMessages(id, messages, context, { keepalive = false } = {}) {
    return conversationRequest(`/${enc(id)}/messages`, context, { method: 'PUT', body: { messages }, keepalive });
  },

  /** Panier de rapport de la conversation (BE-09) : {items, updated_at}. */
  getBasket(id, context, signal) {
    return conversationRequest(`/${enc(id)}/basket`, context, { signal });
  },

  /** Panier complet et ordonné, 8 éléments au plus (idempotent). {ok, changed, items, updated_at} */
  saveBasket(id, items, context, { keepalive = false } = {}) {
    return conversationRequest(`/${enc(id)}/basket`, context, { method: 'PUT', body: { items }, keepalive });
  },
};


// ════════════════════════════════════════════════════════════════════
// Rapport composé « Ajouter au rapport » (Sprint 5, DOC-17)
// Assemblage déterministe des visuels déjà validés : AUCUN appel au modèle.
// ════════════════════════════════════════════════════════════════════

export const reportsAPI = {
  /**
   * POST /api/reports/compose
   * @param {{title, format, conversation_id?, items: [{message_id?, artifact_id, view_type?, spec}]}} body
   * @returns {Promise<object>} spécification `kind: "document"` (contrat v1.3)
   */
  async compose(body, context = null, signal = undefined) {
    const response = await fetch(`${API_BASE_URL}/api/reports/compose`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(hasHistoryContext(context) ? contextHeaders(context) : {}),
      },
      body: JSON.stringify({
        ...body,
        // Comme le chat : le serveur range le fichier dans le dossier de l'entreprise
        ...(context?.entreprise_id
          ? { context: { entreprise_id: context.entreprise_id, user_id: context.user_id ?? null, user_api_hash: context.user_api_hash ?? null } }
          : {}),
      }),
      cache: 'no-store',
      signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const details = Array.isArray(data.details)
        ? data.details.filter((d) => d && typeof d.id === 'string').map((d) => ({ id: d.id, raison: String(d.raison ?? '') }))
        : null;
      throw new ApiError(data.error || data.message || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null, details });
    }
    const spec = documentFromResponse(data);
    if (!spec) throw new ApiError('Réponse du serveur incomplète.', { status: response.status, code: 'INVALID_RESPONSE' });
    return spec;
  },
};


// ════════════════════════════════════════════════════════════════════
// E-mails : aperçu, envoi, annulation et liste (sur un clic de l'utilisateur seulement ;
// l'assistant ne fait que préparer le brouillon). Contexte en en-têtes, adresse de
// l'utilisateur dans le corps (signature).
// ════════════════════════════════════════════════════════════════════

// Connexion coupée (fetch lève un TypeError) : statut 0, la demande a pu arriver au serveur.
// Erreur HTTP : `envoi`, état gardé de la carte quand le serveur le joint (envoi incertain ou
// déjà en cours).
async function emailRequest(path, context, { method = 'GET', body, signal } = {}) {
  if (!hasHistoryContext(context)) {
    throw new ApiError('Ouvrez l’assistant depuis la plateforme digiTRANS pour envoyer un e-mail.', { status: 401, code: 'CONTEXT_REQUIRED' });
  }
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/api/emails${path}`, {
      method,
      headers: { ...contextHeaders(context), ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}) },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      cache: 'no-store',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new ApiError('Connexion au serveur interrompue : l’issue de la demande est inconnue.', { status: 0, code: 'NETWORK' });
  }
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new ApiError(data.error || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
    error.envoi = data.envoi && typeof data.envoi === 'object' ? data.envoi : null;
    throw error;
  }
  return data;
}

export const emailsAPI = {
  /**
   * POST /api/emails/preview → {html, text, lang} : l'e-mail tel qu'il partira (gabarit,
   * signature) ; sans contexte entreprise (hors de la plateforme), aperçu sans signature
   */
  async preview(draft, context, signal) {
    const response = await fetch(`${API_BASE_URL}/api/emails/preview`, {
      method: 'POST',
      headers: { ...(hasHistoryContext(context) ? contextHeaders(context) : {}), 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...draft, context: { email: context?.email ?? null } }),
      cache: 'no-store',
      signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new ApiError(data.error || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
    return data;
  },
  /**
   * POST /api/emails/send → {email: {email_id, statut, envoye_le, programme_pour, erreur},
   * deja_envoye?} ; draft.draft_id (identifiant de la carte) : clé d'idempotence, un brouillon
   * envoyé ou programmé ne repart jamais, un envoi incertain seulement avec renvoyer: true
   */
  send(draft, context, signal) {
    return emailRequest('/send', context, { method: 'POST', body: { ...draft, context: { email: context?.email ?? null } }, signal });
  },
  /** GET /api/emails/envois/:cle → {envoi | null} : état gardé de la carte, sur tout poste */
  state(draftId, context, signal) {
    return emailRequest(`/envois/${encodeURIComponent(String(draftId))}`, context, { signal });
  },
  /** POST /api/emails/verifier → {envoi} : envoi incertain cherché de nouveau dans le suivi des envois */
  verify(draftId, context) {
    return emailRequest('/verifier', context, { method: 'POST', body: { draft_id: draftId } });
  },
  /** POST /api/emails/:id/cancel → {email: {email_id, statut}} ; draftId : carte mise à jour */
  cancel(emailId, context, draftId = null) {
    return emailRequest(`/${encodeURIComponent(String(emailId))}/cancel`, context, { method: 'POST', body: draftId ? { draft_id: draftId } : {} });
  },
  /** GET /api/emails → {items, pagination} */
  list(params = {}, context) {
    const query = new URLSearchParams(Object.entries(params).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)]));
    return emailRequest(query.size ? `?${query}` : '', context);
  },
};

// ════════════════════════════════════════════════════════════════════
// Tableau de bord qualité (Sprint 6, B6 / F4) : GET /api/admin/metrics, et export CSV des avis
// GET /api/admin/feedback.csv (lot administration).
// Clé d'administration dans Authorization: Bearer, jamais dans l'URL ; réponse no-store.
// 404 : tableau de bord désactivé (clé serveur absente ou trop courte) ; 401 : clé refusée ;
// 429 : trop de tentatives (Retry-After, en secondes) ; 400 : période invalide.
// ════════════════════════════════════════════════════════════════════

// Délai d'attente après un 429 : en-tête Retry-After (exposé en même origine ; en
// développement cross-origin, CORS peut le masquer), sinon champ du corps, sinon 15 min
function retryAfterSeconds(response, data) {
  const header = Number(response.headers.get('Retry-After'));
  if (Number.isFinite(header) && header > 0) return header;
  const body = Number(data?.retry_after_s ?? data?.retry_after);
  if (Number.isFinite(body) && body > 0) return body;
  return 15 * 60;
}

export const adminAPI = {
  /**
   * @param {string} key  clé d'administration (ADMIN_METRICS_KEY du serveur)
   * @param {{debut: string, fin: string}} period  dates AAAA-MM-JJ (92 jours au plus)
   * @returns {Promise<object>} agrégats (voir components/admin/qualitySpecs.js)
   */
  async metrics(key, { debut, fin }, signal = undefined) {
    const params = new URLSearchParams({ debut, fin });
    const response = await fetch(`${API_BASE_URL}/api/admin/metrics?${params}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${key}` },
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const error = new ApiError(data.error || data.message || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
      if (response.status === 429) error.retryAfter = retryAfterSeconds(response, data);
      throw error;
    }
    return data;
  },

  /**
   * Export CSV des avis de la période (GET /api/admin/feedback.csv ; lot administration) :
   * séparateur « ; », BOM, sans commentaire libre ni identifiant. Mêmes clé, période et
   * erreurs que metrics.
   * @returns {Promise<Blob>} fichier tel que reçu (lu en Blob : un texte perdrait le BOM)
   */
  async feedbackCsv(key, { debut, fin }, signal = undefined) {
    const params = new URLSearchParams({ debut, fin });
    const response = await fetch(`${API_BASE_URL}/api/admin/feedback.csv?${params}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${key}` },
      cache: 'no-store',
      credentials: 'omit',
      signal,
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      const error = new ApiError(data.error || data.message || `HTTP ${response.status}`, { status: response.status, code: data.code ?? null });
      if (response.status === 429) error.retryAfter = retryAfterSeconds(response, data);
      throw error;
    }
    return response.blob();
  },
};
